<script setup>
import {usePiniaStore} from "../store/pinia_store";
import {computed, onMounted, ref} from "vue";
import _, {findIndex, isEmpty, isNil} from "lodash";
import FavoriteLine from "./FavoriteLine.vue";

const piniaStore = usePiniaStore();

const props = defineProps({
    cartEventBus: {
        type: Object,
        required: true,
    },
    eventBus: {
        type: Object,
        required: true,
    },
    dentaidApiInstance: {
        type: Function,
        required: true,
    },
    langCode: {
        type: String,
        default: 'nl',
    }
})

const loading = ref(true);
const selectedCrmIds = ref([]);
const introText = window.Drupal.t('Dentaid likes to make it easy for you to order products. With the help of <span class="icon-heart"></span> you can easily save products in your list and easily add it to your shopping cart.');
const addToCartText = window.Drupal.t('Add all to cart');
const addSelectedToCartText = window.Drupal.t('Add selected to cart');
const favoritesTitle = window.Drupal.t('Your list of favorite products');
const addToCartMessage = window.Drupal.t('Product(s) succesfully added to cart!');
const addToCartMessageError = window.Drupal.t('Something went wrong when adding products to the cart.');
const noFavoritesText = window.Drupal.t('You have no favorites');
const emptyFavoritesText = window.Drupal.t('Empty favorites list');
const loadingText = window.Drupal.t('Loading...');

const hasFavorites = computed(() => {
    return !isEmpty(piniaStore.favorites?.lines);
})

const hasSelectedCrmIds = computed(() => {
    return !isEmpty(selectedCrmIds.value);
})

const handleClickCart = () => {
    let selectedItems = isEmpty(selectedCrmIds.value) ? piniaStore.favorites.lines : selectedCrmIds.value;

    let items = _.map(selectedItems, function (selectedItem) {
        let quantity = selectedItem.quantity;

        if (isNil(quantity)) {
            let favoriteLineIndex = findIndex(piniaStore.favorites.lines, function (line) {
               return line.sku === selectedItem.sku;
            });

            quantity = piniaStore.cart.lines[selectedItem.sku]?.quantity ?? piniaStore.favorites.lines[favoriteLineIndex]?.min_order_quantity;
        }

        return {
            'sku': selectedItem.sku,
            'quantity': parseInt(quantity),
        }
    });

    props.dentaidApiInstance.post(`/cart?token=${piniaStore.parsedToken}`, {
        items: items
    })
        .then(response => {
            piniaStore.updateUserData(response.data.data);

            Vue.$toast.success(addToCartMessage);
        })
        .catch(error => {
            Vue.$toast.error(addToCartMessageError);
        })
        .finally(() => {
            props.eventBus.$emit('reset-checked-favorites');
        });
}

const handleChecked = (payload) => {
    const index = findIndex(selectedCrmIds.value, function (selected) {
        return selected.sku === payload.sku;
    })

    if (index !== -1) {
        selectedCrmIds.value.splice(index, 1);

        return;
    }

    selectedCrmIds.value.push({
        sku: payload.sku,
        quantity: payload.quantity
    })
}

const updateSelectedQuantity = (payload) => {
    const index = findIndex(selectedCrmIds.value, function (selected) {
        return selected.sku === payload.sku;
    })

    if (index === -1) {
        selectedCrmIds.value.push({
            sku: payload.sku,
            quantity: payload.quantity
        })
    } else {
        selectedCrmIds.value[index].quantity = payload.quantity;
    }
}

const purgeFavorites = () => {
    if (confirm('Are you sure you want to remove all favorites?')) {
        props.dentaidApiInstance.delete(`/favorites/clear?token=${piniaStore.parsedToken}`)
            .then(response => {
                Vue.$toast.success(window.Drupal.t('Successfully emptied favorites.'));

                piniaStore.updateUserData(response.data.data)
            })
            .catch(error => {
                Vue.$toast.error(window.Drupal.t("Something went wrong while emptying favorites, please try again later."))
            });
    }
}

onMounted(() => {
    loading.value = false;
})
</script>

<template>
    <div class="vue-account-favorites">
        <div class="row">
            <div class="col-12 intro">
                <div class="mb-4" v-html="introText"></div>

                <button
                    @click="handleClickCart"
                    :disabled="loading"
                    class="btn-primary mb-2"
                >
                    <template v-if="hasSelectedCrmIds">
                        {{ addSelectedToCartText }}
                    </template>
                    <template v-if="!hasSelectedCrmIds">
                        {{ addToCartText }}
                    </template>
                </button>

                <button @click="purgeFavorites" class="btn-secondary mb-2">{{ emptyFavoritesText }}</button>
            </div>
        </div>

        <div class="row favorite-lines mt-5">
            <div class="col-12">
                <h4 class="mb-3">{{ favoritesTitle }}</h4>

                <FavoriteLine
                    v-if="hasFavorites && !loading"
                    v-for="(favorite, index) in piniaStore.favorites.lines"
                    :key="index"
                    :item="favorite"
                    :jwt-token="piniaStore.parsedToken"
                    :dentaid-api-instance="dentaidApiInstance"
                    :lang-code="langCode"
                    :cart-event-bus="cartEventBus"
                    :parent-event-bus="eventBus"
                    @favorite-selected="handleChecked"
                    @update-selected-quantity="updateSelectedQuantity"
                ></FavoriteLine>

                <div class="row" v-if="!hasFavorites &!loading">
                    <div class="col-12 text-align-center">
                        {{ noFavoritesText }}
                    </div>
                </div>

                <div class="row" v-if="loading">
                    <div class="col-12 text-align-center my-5">
                        {{ loadingText }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>